/**
 * Screenshot utility using html2canvas
 * 
 * Captures the screen (or specific element) and returns base64 image data.
 * Used by the built-in capture_screen client tool.
 */
import html2canvas from 'html2canvas';

/**
 * Default options for html2canvas
 */
const DEFAULT_OPTIONS = {
  useCORS: true,              // Allow cross-origin images
  allowTaint: false,          // Don't allow tainted canvas
  backgroundColor: '#ffffff', // White background (null for transparent)
  scale: 1,                   // 1:1 scale (increase for higher quality)
  logging: false,             // Disable console logging
  imageTimeout: 15000,        // 15s timeout for loading images
};

/**
 * Capture a screenshot of the page or a specific element
 * 
 * @param {Object} options - Capture options
 * @param {string} [options.selector] - CSS selector for element to capture (default: document.body)
 * @param {string} [options.format='jpeg'] - Output format: 'png' or 'jpeg'
 * @param {number} [options.quality=0.85] - JPEG quality (0-1), only used for 'jpeg' format
 * @param {number} [options.scale=1] - Scale factor (2 = 2x resolution)
 * @param {number} [options.maxWidth=1280] - Maximum width (will scale down if larger)
 * @param {number} [options.maxHeight=1280] - Maximum height (will scale down if larger)
 * @param {boolean} [options.fullPage=false] - Capture full scrollable page (not just viewport)
 * @returns {Promise<Object>} Screenshot result with base64 data
 */
export async function captureScreenshot(options = {}) {
  const {
    selector = null,
    format = 'jpeg',
    quality = 0.85,
    scale = 1,
    maxWidth = 1280,
    maxHeight = 1280,
    fullPage = false,
  } = options;

  console.log('📸 SDK Screenshot: Starting capture with options:', options);
  const startTime = Date.now();

  try {
    // Determine target element
    let targetElement = document.body;
    
    if (selector) {
      const element = document.querySelector(selector);
      if (!element) {
        throw new Error(`Element not found: ${selector}`);
      }
      targetElement = element;
      console.log(`📸 SDK Screenshot: Capturing element: ${selector}`);
    } else {
      console.log('📸 SDK Screenshot: Capturing entire page');
    }

    // Build html2canvas options
    const canvasOptions = {
      ...DEFAULT_OPTIONS,
      scale: scale,
    };

    // For full page capture, handle scrolling
    if (fullPage && !selector) {
      canvasOptions.windowHeight = document.documentElement.scrollHeight;
      canvasOptions.height = document.documentElement.scrollHeight;
      canvasOptions.y = 0;
    }

    // Capture the screenshot
    const canvas = await html2canvas(targetElement, canvasOptions);
    console.log(`📸 SDK Screenshot: Canvas created (${canvas.width}x${canvas.height})`);

    // Resize if needed
    let finalCanvas = canvas;
    if (maxWidth || maxHeight) {
      finalCanvas = resizeCanvas(canvas, maxWidth, maxHeight);
      if (finalCanvas !== canvas) {
        console.log(`📸 SDK Screenshot: Resized to ${finalCanvas.width}x${finalCanvas.height}`);
      }
    }

    // Convert to base64
    const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';
    const dataUrl = finalCanvas.toDataURL(mimeType, format === 'jpeg' ? quality : undefined);
    
    // Extract base64 data (remove "data:image/png;base64," prefix)
    const base64Data = dataUrl.split(',')[1];
    
    const captureTimeMs = Date.now() - startTime;
    console.log(`📸 SDK Screenshot: Capture completed in ${captureTimeMs}ms`);
    console.log(`📸 SDK Screenshot: Size: ${Math.round(base64Data.length / 1024)}KB`);

    return {
      image: base64Data,
      mimeType: mimeType,
      width: finalCanvas.width,
      height: finalCanvas.height,
      captureTimeMs: captureTimeMs,
      selector: selector || 'body',
    };

  } catch (error) {
    console.error('📸 SDK Screenshot: Capture failed:', error);
    throw new Error(`Screenshot capture failed: ${error.message}`);
  }
}

/**
 * Resize canvas to fit within max dimensions while maintaining aspect ratio
 * @private
 */
function resizeCanvas(sourceCanvas, maxWidth, maxHeight) {
  let { width, height } = sourceCanvas;
  
  // Calculate scale factor
  let scaleFactor = 1;
  if (maxWidth && width > maxWidth) {
    scaleFactor = Math.min(scaleFactor, maxWidth / width);
  }
  if (maxHeight && height > maxHeight) {
    scaleFactor = Math.min(scaleFactor, maxHeight / height);
  }
  
  // No resize needed
  if (scaleFactor === 1) {
    return sourceCanvas;
  }
  
  // Create resized canvas
  const newWidth = Math.round(width * scaleFactor);
  const newHeight = Math.round(height * scaleFactor);
  
  const resizedCanvas = document.createElement('canvas');
  resizedCanvas.width = newWidth;
  resizedCanvas.height = newHeight;
  
  const ctx = resizedCanvas.getContext('2d');
  ctx.drawImage(sourceCanvas, 0, 0, newWidth, newHeight);
  
  return resizedCanvas;
}

export default captureScreenshot;
